import React, { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";

export default function SetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) return setError("Wachtwoord moet minimaal 8 tekens zijn.");
    if (password !== confirm) return setError("Wachtwoorden komen niet overeen.");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Wachtwoord ingesteld! Je kunt nu inloggen.");
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-slate-900">
      <form onSubmit={submit} className="w-full max-w-sm space-y-5 bg-background rounded-2xl p-8 border" data-testid="set-password-form">
        <div className="flex items-center gap-2.5">
          <img
            src="/kelderke-logo.png"
            alt="'t Kelderke"
            className="w-10 h-10 object-contain invert"
          />
          <p className="font-heading font-bold text-lg">Bar 't Kelderke</p>
        </div>
        <div>
          <h2 className="font-heading text-2xl font-bold">Wachtwoord instellen</h2>
          <p className="text-sm text-muted-foreground mt-1">Activeer je account en kies een wachtwoord.</p>
        </div>
        {!token && (
          <div role="alert" data-testid="set-password-invalid-link" className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">
            Ongeldige uitnodigingslink. Vraag je planner of beheerder om een nieuwe.
          </div>
        )}
        {error && (
          <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid="set-password-error">{error}</div>
        )}
        <div className="space-y-2">
          <Label htmlFor="pw">Nieuw wachtwoord</Label>
          <Input id="pw" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required minLength={8} data-testid="set-password-input" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="pw2">Bevestig wachtwoord</Label>
          <Input id="pw2" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="set-password-confirm" />
        </div>
        <Button type="submit" className="w-full" disabled={busy || !token} data-testid="set-password-submit">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
          Wachtwoord instellen
        </Button>
        <p className="text-sm text-center text-muted-foreground">
          <Link to="/login" data-testid="set-password-login-link" className="text-red-600 font-medium hover:underline">Terug naar inloggen</Link>
        </p>
      </form>
    </div>
  );
}
